import {UploadOutlined} from "@ant-design/icons";
import {Button, Col, Row, Select, Tabs} from "antd";
import {ColumnsType} from "antd/es/table";
import {defaultGeneralRepository, IGeneralRepository,} from "api/repositories/general/GeneralRepository";
import PillTabs from "components/generalComponents/pillTabs/PillTabs";
import StatCard from "components/generalComponents/statCard/StatCard";
import TableComponent from "components/generalComponents/tableComponent/TableComponent";
import {AuthenticationContext} from "context/AuthenticationContext";
import React, {useEffect, useMemo, useState} from "react";
import {NumberFormat} from "utils/format/NumberFormat";
import {strings} from "utils/localizedStrings";
import GeneralViewModel from "../viewModel/GeneralViewModel";

// Icons for stat cards
import RevCurMonthIcon from "../icons/rev_cur_month.png";
import RevPrevMonthIcon from "../icons/rev_prev_month.png";
import RevTodayIcon from "../icons/rev_today.png";
import SaleCurMonthIcon from "../icons/sale_cur_month.png";
import SalePrevMonthIcon from "../icons/sale_prev_month.png";
import SaleTodayIcon from "../icons/sale_today.png";

interface IGeneralFeature {
  generalRepository?: IGeneralRepository;
}

const GeneralFeature: React.FC<IGeneralFeature | any> = (
  props: IGeneralFeature
) => {
  const {
    config,
    transactionReportData,
    transactionReportLoading,
    getTransactionReport,
    transactionDetailReportData,
    transactionDetailReportLoading,
    getTransactionDetailReport,
    exportTransactionDetailReport,
    storeOptions,
    staffOptions,
    selectedStores,
    selectedStaffs,
    setSelectedStores,
    setSelectedStaffs,
    staffLoading,
  } = GeneralViewModel(props?.generalRepository || defaultGeneralRepository);

  const { serviceOptions, user } = AuthenticationContext();
  const finalFilterOptions = useMemo(() => {
    return [
      {
        name: "ALL" , code: "ALL" , displayCode: null
      },
      ...serviceOptions,
    ];
  }, [serviceOptions]);

  // Local UI state for dashboard controls
  const [service, setService] = useState<string>("ALL");
  const [range, setRange] = useState<string>("day");
  // map UI range to API type: 0 = daily, 1 = weekly, 2 = monthly
  const reportType = useMemo(
    () => (range === "day" ? 0 : range === "week" ? 1 : 2),
    [range]
  );

  // Update detail report when filters change
  useEffect(() => {
    getTransactionDetailReport(service, reportType);
  }, [service, selectedStores, selectedStaffs, reportType]);

  useEffect(() => {
    getTransactionReport(service);
  }, [service, selectedStores, selectedStaffs]);

  // Helper function to format change rate
  const formatChangeRate = (rate?: number) => {
    if (rate === undefined || rate === null) return "";
    const sign = rate >= 0 ? "+" : "";
    return `${sign}${rate.toFixed(1)}%`;
  };

  // Process API data for stat cards
  const processedStatCards = useMemo(() => {
    if (!transactionReportData || transactionReportData.length === 0) {
      return {
        todayRevenue: {
          value: NumberFormat.currencyFormat(0),
          change: "",
          trend: "up" as const,
        },
        monthRevenue: {
          value: NumberFormat.currencyFormat(0),
          change: "",
          trend: "up" as const,
        },
        previousMonthRevenue: {
          value: NumberFormat.currencyFormat(0),
          change: "",
          trend: "up" as const,
        },
        todayTransactions: { value: "0", change: "", trend: "up" as const },
        monthTransactions: { value: "0", change: "", trend: "up" as const },
        previousMonthTransactions: {
          value: "0",
          change: "",
          trend: "up" as const,
        },
      };
    }

    const today = transactionReportData?.[0];
    const thisMonth = transactionReportData?.[1];
    const previousMonth = transactionReportData?.[2];

    return {
      todayRevenue: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? NumberFormat.currencyFormat(today.revenue ?? 0)
          : NumberFormat.currencyFormat(0),
        change:
          today?.revenueChangeRate !== undefined &&
          today?.revenueChangeRate !== null
            ? formatChangeRate(today.revenueChangeRate)
            : "",
        trend:
          (today?.revenueChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      monthRevenue: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? NumberFormat.currencyFormat(thisMonth.revenue ?? 0)
          : NumberFormat.currencyFormat(0),
        change:
          thisMonth?.revenueChangeRate !== undefined &&
          thisMonth?.revenueChangeRate !== null
            ? formatChangeRate(thisMonth.revenueChangeRate)
            : "",
        trend:
          (thisMonth?.revenueChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      previousMonthRevenue: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? NumberFormat.currencyFormat(previousMonth.revenue ?? 0)
          : NumberFormat.currencyFormat(0),
        change:
          previousMonth?.revenueChangeRate !== undefined &&
          previousMonth?.revenueChangeRate !== null
            ? formatChangeRate(previousMonth.revenueChangeRate)
            : "",
        trend:
          (previousMonth?.revenueChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      todayTransactions: {
        currentTime: today?.currentTime ?? strings.General.Revenue.CurrentDate,
        previousTime:
          today?.previousTime ?? strings.General.Revenue.PreviousDate,
        value: today
          ? (today.quantity ?? 0).toLocaleString()
          : NumberFormat.currencyFormat(0),
        change:
          today?.quantityChangeRate !== undefined &&
          today?.quantityChangeRate !== null
            ? formatChangeRate(today.quantityChangeRate)
            : "",
        trend:
          (today?.quantityChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      monthTransactions: {
        currentTime:
          thisMonth?.currentTime ?? strings.General.Revenue.CurrentMonth,
        previousTime:
          thisMonth?.previousTime ?? strings.General.Revenue.LastMonth,
        value: thisMonth
          ? (thisMonth.quantity ?? 0).toLocaleString()
          : NumberFormat.currencyFormat(0),
        change:
          thisMonth?.quantityChangeRate !== undefined &&
          thisMonth?.quantityChangeRate !== null
            ? formatChangeRate(thisMonth.quantityChangeRate)
            : "",
        trend:
          (thisMonth?.quantityChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      previousMonthTransactions: {
        currentTime:
          previousMonth?.currentTime ?? strings.General.Revenue.LastMonth,
        previousTime:
          previousMonth?.previousTime ??
          strings.General.Revenue.LastMonthBefore,
        value: previousMonth
          ? (previousMonth.quantity ?? 0).toLocaleString()
          : NumberFormat.currencyFormat(0),
        change:
          previousMonth?.quantityChangeRate !== undefined &&
          previousMonth?.quantityChangeRate !== null
            ? formatChangeRate(previousMonth.quantityChangeRate)
            : "",
        trend:
          (previousMonth?.quantityChangeRate || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
    };
  }, [transactionReportData]);

  // Build dynamic period columns from API stat times
  const periodLabels = useMemo(() => {
    const formatDay = (dateStr: string) => {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };
    const formatWeek = (dateStr: string) => {
      const trimmed = String(dateStr).trim();
      const weekNum = Number(trimmed);
      if (!isNaN(weekNum) && /^\d+$/.test(trimmed)) {
        return `${strings.General.Revenue.WeekOfYear} ${weekNum}`;
      }
      const d = new Date(trimmed);
      if (isNaN(d.getTime())) return trimmed;
      const dayIdx = d.getDay();
      const diff = (dayIdx === 0 ? -6 : 1) - dayIdx; // Monday
      d.setDate(d.getDate() + diff);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${strings.General.Revenue.WeekOfYear} ${day}-${month}-${year}`;
    };
    const formatMonth = (dateStr: string) => {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${month}-${year}`;
    };

    const formatter =
      range === "day" ? formatDay : range === "week" ? formatWeek : formatMonth;
    const first = transactionDetailReportData?.[0];
    const stats = first?.stat || [];
    return stats.map((s: any) => formatter(s.time));
  }, [transactionDetailReportData, range]);

  type RowType = { name: string } & Record<string, number | string>;

  // Process API data for tables
  const processedTableData = useMemo(() => {
    if (
      !transactionDetailReportData ||
      transactionDetailReportData.length === 0
    ) {
      return [];
    }

    const formatLabel = (time: string) => {
      const trimmed = String(time).trim();
      const weekNum = Number(trimmed);
      const d = new Date(trimmed);
      const isValidDate = !isNaN(d.getTime());
      if (range === "day") {
        if (!isValidDate) return trimmed;
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      } else if (range === "week") {
        if (!isNaN(weekNum) && /^\d+$/.test(trimmed)) {
          return `${strings.General.Revenue.WeekOfYear} ${weekNum}`;
        }
        if (!isValidDate) return trimmed;
        const dayIdx = d.getDay();
        const diff = (dayIdx === 0 ? -6 : 1) - dayIdx;
        d.setDate(d.getDate() + diff);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${strings.General.Revenue.WeekOfYear} ${day}-${month}-${year}`;
      } else {
        if (!isValidDate) return trimmed;
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${month}-${year}`;
      }
    };

    return transactionDetailReportData.map((item, index) => {
      const statMap: Record<string, number> = (item.stat || []).reduce(
        (acc, s: any) => {
          acc[formatLabel(s.time)] = Number(s.quantity || 0);
          return acc;
        },
        {} as Record<string, number>
      );

      const totalTransactions = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.quantity || 0),
        0
      );
      const totalRevenue = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.revenue || 0),
        0
      );

      return {
        key: index.toString(),
        name: item.name || item.service || `Item ${index + 1}`,
        service: item.service,
        carrier: item.carrier,
        ...periodLabels.reduce(
          (acc, label) => ({
            ...acc,
            [label]: statMap[label] || 0,
          }),
          {} as Record<string, number>
        ),
        totalRevenue: totalRevenue,
        totalTransactions: totalTransactions,
      };
    });
  }, [transactionDetailReportData, periodLabels]);

  // Process revenue data for revenue table
  const processedRevenueData = useMemo(() => {
    if (
      !transactionDetailReportData ||
      transactionDetailReportData.length === 0
    ) {
      return [];
    }

    const formatLabel = (time: string) => {
      const trimmed = String(time).trim();
      const weekNum = Number(trimmed);
      const d = new Date(trimmed);
      const isValidDate = !isNaN(d.getTime());
      if (range === "day") {
        if (!isValidDate) return trimmed;
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      } else if (range === "week") {
        if (!isNaN(weekNum) && /^\d+$/.test(trimmed)) {
          return `${strings.General.Revenue.WeekOfYear} ${weekNum}`;
        }
        if (!isValidDate) return trimmed;
        const dayIdx = d.getDay();
        const diff = (dayIdx === 0 ? -6 : 1) - dayIdx;
        d.setDate(d.getDate() + diff);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${strings.General.Revenue.WeekOfYear} ${day}-${month}-${year}`;
      } else {
        if (!isValidDate) return trimmed;
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${month}-${year}`;
      }
    };

    return transactionDetailReportData.map((item, index) => {
      const statMap: Record<string, number> = (item.stat || []).reduce(
        (acc, s: any) => {
          acc[formatLabel(s.time)] = Number(s.revenue || 0);
          return acc;
        },
        {} as Record<string, number>
      );

      const totalRevenue = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.revenue || 0),
        0
      );
      const totalTransactions = (item.stat || []).reduce(
        (sum, s: any) => sum + Number(s.quantity || 0),
        0
      );

      return {
        key: index.toString(),
        name: item.name || item.service || `Item ${index + 1}`,
        service: item.service,
        carrier: item.carrier,
        ...periodLabels.reduce(
          (acc, label) => ({
            ...acc,
            [label]: statMap[label] || 0,
          }),
          {} as Record<string, number>
        ),
        totalRevenue: totalRevenue,
        totalTransactions: totalTransactions,
      };
    });
  }, [transactionDetailReportData, periodLabels]);

  const buildColumns = (
    title: string,
    valueType: "currency" | "number" = "currency"
  ): ColumnsType<RowType> => {
    const dynamicCols: ColumnsType<RowType> = periodLabels.map((label) => ({
      title: label,
      dataIndex: label,
      align: "right",
      width: 200,
      render: (val: any) =>
        valueType === "currency"
          ? NumberFormat.currencyFormat(Number(val || 0))
          : Number(val || 0).toLocaleString(),
    }));
    return [
      {
        title: "Danh mục",
        dataIndex: "name",
        fixed: "left",
        width: 150,
      },
      ...dynamicCols,
    ];
  };

  // Use real data from API only
  const mockData: RowType[] = useMemo(
    () => processedTableData,
    [processedTableData]
  );

  const revenueData: RowType[] = useMemo(
    () => processedRevenueData,
    [processedRevenueData]
  );

  const revenueColumns = useMemo(
    () => buildColumns("Doanh thu", "currency"),
    [periodLabels]
  );
  const volumeColumns = useMemo(
    () => buildColumns("Sản lượng", "number"),
    [periodLabels]
  );

  const handleExport = async () => {
    const reportType = range === "day" ? 0 : range === "week" ? 1 : 2;
    const blob = await exportTransactionDetailReport(service, reportType, {
      store: selectedStores.filter(Boolean) as any,
      staff: selectedStaffs.filter(Boolean) as any,
    });
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `dashboard_${service}_${range}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Row gutter={[8, 8]} className="bg-transparent p-4">
      {/* Page headings */}
      <Col span={24}>
        <div className="flex flex-col gap-1 mb-1">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
            {strings.General.Title}
          </h2>
          <p className="text-sm text-gray-500">{strings.General.Summary}</p>
        </div>
      </Col>
      {/* Top filter: service selector */}
      <Col span={24}>
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <PillTabs
            value={service}
            onChange={(val) => setService(String(val))}
            options={finalFilterOptions.map((item) => ({
              label:
                strings.GlobalService[
                  item.code as keyof typeof strings.GlobalService
                ],
              value: item.code,
            }))}
          />
          <div className="flex gap-3">
            {user?.type === 3 && (
              <Select
                value={selectedStores[0] ?? undefined}
                onChange={(v) => {
                  const value = (v as string) ?? "";
                  if (value === "") {
                    // Chọn "Tất cả" => reset cả Store và Staff
                    setSelectedStores([]);
                    setSelectedStaffs([]);
                  } else {
                    // Mỗi lần chọn cửa hàng thì reset filter nhân viên
                    setSelectedStaffs([]);
                    setSelectedStores([value]);
                  }
                }}
                className="min-w-[260px]"
                options={storeOptions}
                placeholder={"Cửa hàng"}
                showSearch
                optionFilterProp="label"
              />
            )}
            {(user?.type === 3 || user?.type === 4) && (
              <Select
                mode="multiple"
                value={selectedStaffs}
                onChange={(v) => {
                  const values = (v as string[]) || [];
                  if (values.includes("")) {
                    // Chọn "Tất cả" => reset Staff
                    setSelectedStaffs([]);
                  } else {
                    setSelectedStaffs(values.filter((s) => !!s));
                  }
                }}
                className="min-w-[260px]"
                options={staffOptions}
                placeholder={"Nhân viên"}
                disabled={
                  !!staffLoading || (user?.type === 3 ? selectedStores.length === 0 : false)
                }
                showSearch
                optionFilterProp="label"
                maxTagCount={2}
              />
            )}
          </div>
        </div>
      </Col>

      {/* Stat Cards */}
      <Col span={24}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <StatCard
            title={`${strings.General.Revenue.Label} ${processedStatCards.todayRevenue.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.todayRevenue.value
            }
            trend={
              processedStatCards.todayRevenue.change
                ? {
                    direction: processedStatCards.todayRevenue.trend,
                    text: `${processedStatCards.todayRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.todayRevenue.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img src={RevTodayIcon} alt="Revenue Today" className="w-5 h-5" />
            }
            iconContainerClassName="bg-green-50"
            iconColorClassName="text-green-600"
          />
          <StatCard
            title={`${strings.General.Revenue.Label} ${processedStatCards.monthRevenue.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.monthRevenue.value
            }
            trend={
              processedStatCards.monthRevenue.change
                ? {
                    direction: processedStatCards.monthRevenue.trend,
                    text: `${processedStatCards.monthRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.monthRevenue.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img
                src={RevCurMonthIcon}
                alt="Revenue Current Month"
                className="w-5 h-5"
              />
            }
            iconContainerClassName="bg-blue-50"
            iconColorClassName="text-blue-600"
          />
          <StatCard
            title={`${strings.General.Revenue.Label} ${processedStatCards.previousMonthRevenue.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.previousMonthRevenue.value
            }
            trend={
              processedStatCards.previousMonthRevenue.change
                ? {
                    direction: processedStatCards.previousMonthRevenue.trend,
                    text: `${processedStatCards.previousMonthRevenue.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.previousMonthRevenue.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img
                src={RevPrevMonthIcon}
                alt="Revenue Previous Month"
                className="w-5 h-5"
              />
            }
            iconContainerClassName="bg-[#E2702A]/15"
            iconColorClassName="text-[#E2702A]"
          />
          <StatCard
            title={`${strings.General.Revenue.Transaction} ${processedStatCards.todayTransactions.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.todayTransactions.value
            }
            trend={
              processedStatCards.todayTransactions.change
                ? {
                    direction: processedStatCards.todayTransactions.trend,
                    text: `${processedStatCards.todayTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.todayTransactions.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img
                src={SaleTodayIcon}
                alt="Transactions Today"
                className="w-5 h-5"
              />
            }
            iconContainerClassName="bg-purple-50"
            iconColorClassName="text-purple-600"
          />
          <StatCard
            title={`${strings.General.Revenue.Transaction} ${processedStatCards.monthTransactions.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.monthTransactions.value
            }
            trend={
              processedStatCards.monthTransactions.change
                ? {
                    direction: processedStatCards.monthTransactions.trend,
                    text: `${processedStatCards.monthTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.monthTransactions.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img
                src={SaleCurMonthIcon}
                alt="Transactions Current Month"
                className="w-5 h-5"
              />
            }
            iconContainerClassName="bg-rose-50"
            iconColorClassName="text-rose-600"
          />
          <StatCard
            title={`${strings.General.Revenue.Transaction} ${processedStatCards.previousMonthTransactions.currentTime}`}
            value={
              transactionReportLoading
                ? "Loading..."
                : processedStatCards.previousMonthTransactions.value
            }
            trend={
              processedStatCards.previousMonthTransactions.change
                ? {
                    direction:
                      processedStatCards.previousMonthTransactions.trend,
                    text: `${processedStatCards.previousMonthTransactions.change} ${strings.General.Revenue.CompareWith} ${processedStatCards.previousMonthTransactions.previousTime}`,
                  }
                : undefined
            }
            icon={
              <img
                src={SalePrevMonthIcon}
                alt="Transactions Previous Month"
                className="w-5 h-5"
              />
            }
            iconContainerClassName="bg-[#10DDDD]/15"
            iconColorClassName="text-[#10DDDD]"
          />
        </div>
      </Col>

      {/* Time filter + Export below cards */}
      <Col span={service === "ALL" ? 0 : 24}>
        <div className="flex items-center justify-between gap-6 mt-2 mb-2 xl:overflow-x-auto overflow-x-scroll">
          <button
            onClick={() => setRange("day")}
            className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
              range === "day"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          >
            {strings.General.ByDate}
          </button>
          <button
            onClick={() => setRange("week")}
            className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
              range === "week"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          >
            {strings.General.ByWeek}
          </button>
          <button
            onClick={() => setRange("month")}
            className={`min-w-[260px] h-10 rounded-md px-6 font-medium transition-colors ${
              range === "month"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          >
            {strings.General.ByMonth}
          </button>
          <Button
            type="primary"
            className="h-10 rounded-md flex-1"
            style={{ backgroundColor: "#16a34a" }}
            icon={<UploadOutlined />}
            onClick={handleExport}
          >
            {strings.GlobalLabels.ExcelExporting}
          </Button>
        </div>
      </Col>

      {/* Tables with Ant Design Tabs */}
      <Col span={service === "ALL" ? 0 : 24}>
        <div className="bg-white rounded-lg shadow p-4">
          <Tabs
            defaultActiveKey="revenue"
            centered
            items={[
              {
                key: "revenue",
                label: strings.General.Revenue.Label,
                children: (
                  <TableComponent<RowType>
                    data={{
                      columns: revenueColumns,
                      dataSource: revenueData,
                      loading: transactionDetailReportLoading,
                      loadingTitle: strings.GlobalLabels.Loading,
                      page: 0,
                      pageSize: revenueData.length || 10,
                      total: revenueData.length,
                      totalTitle: strings.GlobalLabels.Total,
                      scroll: { x: null, y: 600 },
                    }}
                  />
                ),
              },
              {
                key: "volume",
                label: strings.General.Revenue.Transaction,
                children: (
                  <TableComponent<RowType>
                    data={{
                      columns: volumeColumns,
                      dataSource: mockData,
                      loading: transactionDetailReportLoading,
                      loadingTitle: strings.GlobalLabels.Loading,
                      page: 0,
                      pageSize: mockData.length || 10,
                      total: mockData.length,
                      totalTitle: strings.GlobalLabels.Total,
                      scroll: { x: null, y: 600 },
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      </Col>
    </Row>
  );
};
export default GeneralFeature;
